import { Injectable, OnDestroy } from '@angular/core';
import { Entity, MappedEntityModel, NxDatabase, NxTable } from '@nx-data/nx-data';
import { ClassType } from '@nx-data/utilities';
import { combineLatest, Observable } from 'rxjs';
import { NgxTable } from './ngx-table';

@Injectable()
export class NgxDatabase extends NxDatabase implements OnDestroy {
  public static forEntities(...entities: ClassType<Entity>[]): NgxDatabase {
    const db = new NgxDatabase();
    entities.unique().forEach((it) => new NgxTable(it.name, it, db));
    return db;
  }

  public override createTable<T extends Entity>(classType: ClassType<T>, tableName: string): NxTable<T> {
    return new NgxTable(tableName, classType, this);
  }

  override registerTable<T extends Entity>(classType: ClassType<T>, table: NgxTable<T>) {
    super.registerTable(classType, table);
  }

  override getTable<T extends Entity>(classType: ClassType<T>): NgxTable<T> {
    return super.getTable(classType) as NgxTable<T>;
  }

  observeTable$<T extends Entity>(classType: ClassType<T>): Observable<MappedEntityModel<T>[]> {
    return this.getTable(classType).entities$;
  }

  observeTables$<T1 extends Entity>(classType: ClassType<T1>): Observable<[MappedEntityModel<T1>[]]>;
  observeTables$<T1 extends Entity, T2 extends Entity>(
    classType: ClassType<T1>,
    classType2: ClassType<T2>
  ): Observable<[MappedEntityModel<T1>[], MappedEntityModel<T2>[]]>;
  observeTables$<T1 extends Entity, T2 extends Entity, T3 extends Entity>(
    classType: ClassType<T1>,
    classType2: ClassType<T2>,
    classType3: ClassType<T3>
  ): Observable<[MappedEntityModel<T1>[], MappedEntityModel<T2>[], MappedEntityModel<T3>[]]>;
  observeTables$<T1 extends Entity, T2 extends Entity, T3 extends Entity, T4 extends Entity>(
    classType: ClassType<T1>,
    classType2: ClassType<T2>,
    classType3: ClassType<T3>,
    classType4: ClassType<T4>
  ): Observable<[MappedEntityModel<T1>[], MappedEntityModel<T2>[], MappedEntityModel<T3>[], MappedEntityModel<T4>[]]>;
  observeTables$<T1 extends Entity, T2 extends Entity, T3 extends Entity, T4 extends Entity, T5 extends Entity>(
    classType: ClassType<T1>,
    classType2: ClassType<T2>,
    classType3: ClassType<T3>,
    classType4: ClassType<T4>,
    classType5: ClassType<T5>
  ): Observable<
    [MappedEntityModel<T1>[], MappedEntityModel<T2>[], MappedEntityModel<T3>[], MappedEntityModel<T4>[], MappedEntityModel<T5>[]]
  >;
  observeTables$(...classTypes: ClassType<Entity>[]): Observable<MappedEntityModel<Entity>[][]> {
    return combineLatest(classTypes.map((it) => this.observeTable$(it)));
  }

  public ngOnDestroy(): void {
    this.dispose();
  }
}
